import {
  HOME_VIEW_MODE_STORAGE_KEY,
  useGetPreference,
  useGlobalStore,
  useSetHomeViewModePreference,
  useSyncPreferenceAcrossTabs,
} from "controller";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { HomeViewMode } from "shared";

/**
 * Which home screen the user picked from the profile menu. The server is the
 * source of truth (`settings.home.view_mode` on the resource preference doc),
 * but localStorage mirrors the last known-good value under
 * `HOME_VIEW_MODE_STORAGE_KEY` and is rendered first so a returning user never
 * waits on the request. Only a browser that has never seen a mode blocks on the
 * API, and falls back to the legacy dashboard if it fails.
 */

/** Fired on write so every consumer in *this* tab re-reads the cache
 * immediately (the native `storage` event only fires in *other* tabs). */
const HOME_VIEW_MODE_EVENT = "home-view-mode-change";

/** Last mode the server confirmed, or `null` if this browser has never seen one. */
export const readCachedHomeViewMode = (): HomeViewMode | null => {
  const cached = localStorage.getItem(HOME_VIEW_MODE_STORAGE_KEY);
  return cached === "ai" || cached === "legacy" ? cached : null;
};

export const writeCachedHomeViewMode = (mode: HomeViewMode) => {
  if (readCachedHomeViewMode() === mode) {
    return;
  }
  localStorage.setItem(HOME_VIEW_MODE_STORAGE_KEY, mode);
  window.dispatchEvent(new Event(HOME_VIEW_MODE_EVENT));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(HOME_VIEW_MODE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(HOME_VIEW_MODE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
};

interface UseHomeViewModeResult {
  mode: HomeViewMode;
  /**
   * The preference request is still in flight and there is nothing cached to
   * render in the meantime.
   */
  isResolving: boolean;
  setMode: (mode: HomeViewMode) => void;
  isUpdating: boolean;
}

export const useHomeViewMode = (): UseHomeViewModeResult => {
  const { data, isLoading } = useGetPreference();
  const { mutate: persistMode, isLoading: isUpdating } =
    useSetHomeViewModePreference();
  const cachedMode = useSyncExternalStore(subscribe, readCachedHomeViewMode);
  const isHelenaActive = useGlobalStore((state) => state.isHelenaActive);

  useSyncPreferenceAcrossTabs();

  const serverMode = data?.settings?.home?.view_mode?.mode;

  // Keep the fallback in step with the server so a later outage restores the
  // right screen rather than a stale one.
  useEffect(() => {
    if (serverMode) {
      writeCachedHomeViewMode(serverMode);
    }
  }, [serverMode]);

  const setMode = useCallback(
    (mode: HomeViewMode) => {
      writeCachedHomeViewMode(mode);
      persistMode(mode);
    },
    [persistMode],
  );

  return {
    // Cache first so a returning user renders instantly; the server value takes
    // over once it lands. With no saved mode, Helena-enabled MSPs land on AI.
    mode: cachedMode ?? serverMode ?? (isHelenaActive ? "ai" : "legacy"),
    isResolving: isLoading && cachedMode === null,
    setMode,
    isUpdating,
  };
};
